import {
  computed,
  ComputedRef,
  onMounted,
  Ref,
  ref,
  useContext,
  useRoute,
  useStore,
} from '@nuxtjs/composition-api';
import { TokenPayload } from '@recurly/recurly-js';
import { ValidPaymentCode } from './usePaymentMethods';
import { State } from '~/store';
import { isRecurlyPlanCodeForLocation, RecurlyPlanCode, Plan } from '~/types/plan';
import { User } from '~/types/user';
import { plans } from '~/data/plans';

export interface Address {
  address1: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

interface UseUpgrade {
  isPurchaseLoading: Ref<boolean>;
  coupon: Ref<string | undefined>;
  couponError: Ref<string | undefined>;
  giftCard: Ref<string | undefined>;
  giftCardError: Ref<string | undefined>;
  paymentMethod: Ref<ValidPaymentCode>;
  billingAddress: Ref<Address>;
  selectedPlan: ComputedRef<Plan>;
  selectedPlanCode: Ref<RecurlyPlanCode>;
  purchaseNewSubscription: (
    token: TokenPayload,
    plan: RecurlyPlanCode,
    price?: string,
    referralCode?: string
  ) => Promise<void>;
  upgradeSubscription(plan: RecurlyPlanCode, price?: string, referralCode?: string): Promise<void>;
  getPriceDiffForPlan(planCode: RecurlyPlanCode): Promise<number>;
}

export const useUpgrade = (): UseUpgrade => {
  const store = useStore<State>();
  const { $subscriptionApi, $auth } = useContext();
  const route = useRoute();

  const isPurchaseLoading = ref<boolean>(false);
  const coupon = ref<string | undefined>(undefined);
  const couponError = ref<string | undefined>(undefined);
  const giftCard = ref<string | undefined>(undefined);
  const giftCardError = ref<string | undefined>(undefined);
  const paymentMethod = ref<ValidPaymentCode>('credit-card');
  const billingAddress = ref<Address>({
    address1: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
  });

  const loggedInUser = computed<User>(() => store.getters.loggedInUser);
  const location = computed(() => store.state.location);

  const selectedPlanCode = ref<RecurlyPlanCode>('plus_annual');
  const selectedPlan = computed<Plan>(
    () => plans[location.value.secondary].find(p => p.slug === selectedPlanCode.value) as Plan
  );

  const purchaseNewSubscription = async (
    token: TokenPayload,
    plan: RecurlyPlanCode,
    price?: string,
    referralCode?: string
  ): Promise<void> => {
    trackPaymentCheckoutStarted(price);
    const upgrade = {
      currency: location.value?.currency || loggedInUser.value.location.currency,
      userId: loggedInUser.value.id.toString(),
      email: loggedInUser.value.email,
      firstName: loggedInUser.value.first_name,
      lastName: loggedInUser.value.last_name,
      tokenId: token.id,
      planCode: plan,
      coupons: coupon.value ? [coupon.value] : [],
      giftCard: giftCard.value,
      referralCode: referralCode || undefined,
    };
    await $subscriptionApi.create(upgrade);
    await pollForSubscriptionUpdate();
    if (process.client && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'subscriptionPurchased', {
        userUuid: loggedInUser.value.uuid,
        eventAction: 'Subscription Purchased',
        planName: loggedInUser.value.subscription?.name,
        userCountry: loggedInUser.value.location.country,
        userCurrency: loggedInUser.value.location.currency,
      });
    }
    isPurchaseLoading.value = false;
  };

  const upgradeSubscription = async (
    plan: RecurlyPlanCode,
    price?: string,
    referralCode?: string
  ): Promise<void> => {
    if (!loggedInUser.value?.subscription?.gateway_subscription_id) {
      throw new Error('No subscription ID found');
    }

    trackPaymentCheckoutStarted(price);
    const upgrade = {
      planCode: plan,
      subscriptionId: loggedInUser.value.subscription.gateway_subscription_id,
      userId: loggedInUser.value.id.toString(),
      currency: location.value?.currency || undefined,
      referralCode: referralCode || undefined,
    };
    await $subscriptionApi.upgrade(upgrade);
    await pollForSubscriptionUpdate();
    if (process.client && typeof window.gtag !== 'undefined') {
      window.gtag('event', 'subscriptionPurchased', {
        userUuid: loggedInUser.value.uuid,
        eventAction: 'Subscription Purchased',
        planName: loggedInUser.value.subscription?.name,
        userCountry: loggedInUser.value.location.country,
        userCurrency: loggedInUser.value.location.currency,
      });
    }
    isPurchaseLoading.value = false;
  };

  const getPriceDiffForPlan = async (planCode: RecurlyPlanCode): Promise<number> => {
    if (!loggedInUser.value?.subscription?.gateway_subscription_id) {
      throw new Error('No subscription ID found');
    }
    const priceDiff = {
      planCode,
      subscriptionId: loggedInUser.value.subscription.gateway_subscription_id,
      userId: loggedInUser.value.id.toString(),
    };
    const response = await $subscriptionApi.upgradePreview(priceDiff);
    return response.priceDifference;
  };

  const pollForSubscriptionUpdate = (): Promise<void> => {
    const initialSubscription = loggedInUser.value?.subscription;
    let numRetries = 15;

    // There might be a better way to do this but works for now
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        const response = await $auth.fetchUser();
        if (!response) {
          return;
        }
        const userData = response.data;
        numRetries -= 1;
        if (userData.user.subscription.code !== initialSubscription?.code) {
          clearInterval(interval);
          resolve();
        } else if (numRetries === 0) {
          clearInterval(interval);
          reject(
            new Error('Subscription update timed out. Please wait a few minutes and refresh.')
          );
        }
      }, 1500);
    });
  };

  const trackPaymentCheckoutStarted = (amount?: string): void => {
    // This shoulnd't happen but not explicitly erroring elsewhere
    // so we don't block payments going through. This doesn't get sent to the API
    // so it would only affect tracking.
    if (!amount) {
      console.error('No amount found for plan in recurly');
    }
  };

  onMounted(() => {
    const queryPlan =
      typeof route.value.query?.plan === 'string' ? route.value.query?.plan : undefined;
    if (queryPlan && isRecurlyPlanCodeForLocation(queryPlan, location.value.secondary)) {
      selectedPlanCode.value = queryPlan;
    }
  });

  return {
    isPurchaseLoading,
    coupon,
    couponError,
    giftCard,
    giftCardError,
    paymentMethod,
    billingAddress,
    selectedPlan,
    selectedPlanCode,
    purchaseNewSubscription,
    upgradeSubscription,
    getPriceDiffForPlan,
  };
};
